

import { DatePicker } from "rsuite";
import "rsuite/DatePicker/styles/index.css";
import { kindOf } from "../../lib/utility";

const toLocalDateString = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const toLocalDateTimeMinuteString = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const parseBoundaryDate = (value, boundaryType) => {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  let normalizedValue = value;
  if (boundaryType === "max" && typeof value === "string") {
    const utcToday = new Date().toISOString().slice(0, 10);
    if (value === utcToday) {
      normalizedValue = toLocalDateString();
    }

    const utcNowMinute = new Date().toISOString().slice(0, 16);
    if (value === utcNowMinute) {
      normalizedValue = toLocalDateTimeMinuteString();
    }
  }

  const hasExplicitTime =
    typeof normalizedValue === "string" &&
    (normalizedValue.includes("T") ||
      /^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}/.test(normalizedValue));
  if (hasExplicitTime) {
    const parsed = new Date(normalizedValue);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const parsed = new Date(
    `${normalizedValue} ${
      boundaryType === "max" ? "23:59:59.999" : "00:00:00.000"
    }`
  );
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const getStartOfDay = (date) => {
  const value = new Date(date);
  value.setHours(0, 0, 0, 0);
  return value;
};

const getEndOfDay = (date) => {
  const value = new Date(date);
  value.setHours(23, 59, 59, 999);
  return value;
};


export const reportDateFormatter = (date, type) => {
  const showTimeOption = type === "datetime-local";
  const isMonth = type === "month";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  const hour = String(date.getHours()).padStart(2, "0");
  const minute = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");
  const milliseconds = String(date.getMilliseconds()).padStart(3, "0");

  return (
    `${year}-${month}${!isMonth ? `-${day}` : ""}` +
    (showTimeOption ? ` ${hour}:${minute}:${seconds}.${milliseconds}` : "")
  );
};

function ReportDateTimePicker({
  type,
  id,
  name,
  value,
  onChange,
  max,
  min,
  disabled,
  placeholder,
}) {
  const maxDate = parseBoundaryDate(max, "max");
  const minDate = parseBoundaryDate(min, "min");
  const showTimeOption = type === "datetime-local";
  const isMonth = type === "month";

  const handleDateChange = (date) => {
    if (!date || !(date instanceof Date) || Number.isNaN(date.getTime())) {
      kindOf(onChange, "function") &&
        onChange({
          target: {
            name,
            id,
            value: "",
          },
        });
      return;
    }

    if (showTimeOption) {
      const now = new Date();
      const isToday =
        date.getFullYear() === now.getFullYear() &&
        date.getMonth() === now.getMonth() &&
        date.getDate() === now.getDate();

      const isPrevious =
        new Date(date.getFullYear(), date.getMonth(), date.getDate()) <
        new Date(now.getFullYear(), now.getMonth(), now.getDate());

      if (id === "endDate" || name === "endDate") {
        if (isToday) {
          date.setHours(now.getHours(), now.getMinutes(), now.getSeconds(), 0);
        } else if (isPrevious) {
          date.setHours(23, 59, 59, 0);
        }
      } else if (id === "startDate" || name === "startDate") {
        date.setHours(0, 0, 0, 0);
      }
    }

    const formattedDate = reportDateFormatter(date, type);

    kindOf(onChange, "function") &&
      onChange({
        target: {
          name,
          id,
          value: formattedDate,
        },
      });
  };

  const handleDisableDate = (date) => {
    if (!min && !max) return false;

    if (minDate && date < getStartOfDay(minDate)) return true;
    if (maxDate && date > getEndOfDay(maxDate)) return true;
    return false;
  };

  const parsedValue = value ? new Date(value) : null;
  const pickerValue =
    parsedValue && !Number.isNaN(parsedValue.getTime()) ? parsedValue : null;

  return (
    <>
      <style>{`
        .rs-input-group {
            border: solid 1px #ced4da !important;
            border-radius: 4px;
            overflow: hidden;
        }
        .dark-mode .rs-input-group {    
            border: solid 1px #32334D !important;
        }
        .rs-input-group input {
            padding: 8px 12px !important;
            border-radius: 2px;
            font-size: 16px !important;
        }
        .rs-input-group-addon {display: none !important;}
        .rs-picker-popup {
            z-index: 70000 !important;
        }
        .rs-calendar-header {display: flex;}
        .rs-calendar-header-month-toolbar {
            flex: 1;
            display: flex;
            gap: 6px;
            justify-content: space-between;
        }
        .rs-calendar-table-cell-disabled .rs-calendar-table-cell-content {
            text-decoration: line-through;
        }
      `}</style>

      <DatePicker
        placement="auto"
        oneTap
        className="w-100"
        format={
          (!isMonth ? "dd-" : "") +
          (isMonth ? "MMM-yyyy" : "MM-yyyy") +
          (showTimeOption ? " HH:mm:ss.SSS" : "")
        }
        onChange={handleDateChange}
        onSelect={handleDateChange}
        value={pickerValue}
        placeholder={
          placeholder ?? "Select Date" + (showTimeOption ? " & Time" : "")
        }
        shouldDisableDate={handleDisableDate}
        disabled={disabled}
      />
    </>
  );
}

export default ReportDateTimePicker;
